// ============================================================
// AnnotationField
// ============================================================
// A reusable inline-editable annotation.
// Used for both section notes and bar notes.
//
// Behaviour:
//   - If note is set: shows it in small italic text
//   - If note is null: shows nothing (or a faint placeholder on hover)
//   - Click to edit, Enter/blur to commit, Escape to cancel
//   - Empty string on commit → sets note to null (removes it)
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';

interface AnnotationFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Additional class names for the display text */
  className?: string;
  /** Show the placeholder hint even when value is null */
  showHint?: boolean;
}

export function AnnotationField({
  value,
  onChange,
  placeholder = 'Add note…',
  className = '',
  showHint = false,
}: AnnotationFieldProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value ?? '');
  const inputRef              = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    onChange(trimmed || null);
    setEditing(false);
  }, [draft, onChange]);

  const cancel = useCallback(() => {
    setDraft(value ?? '');
    setEditing(false);
  }, [value]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter')  { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          // Stop Tab/arrow from triggering bar navigation
          if (['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            e.stopPropagation();
          }
        }}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontSize: 11,
          color: 'var(--color-ink-secondary)',
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid var(--color-accent)',
          outline: 'none',
          width: '100%',
          padding: '1px 0',
          lineHeight: 1.3,
        }}
        spellCheck={false}
      />
    );
  }

  if (value) {
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        title="Click to edit annotation"
        className={className}
        style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontSize: 11,
          color: 'var(--color-ink-secondary)',
          cursor: 'text',
          display: 'block',
          lineHeight: 1.3,
          userSelect: 'none',
        }}
      >
        {value}
      </span>
    );
  }

  if (showHint) {
    return (
      <span
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          color: 'var(--color-ink-muted)',
          opacity: 0.4,
          cursor: 'text',
          display: 'block',
          lineHeight: 1.3,
          userSelect: 'none',
          letterSpacing: '0.04em',
        }}
      >
        {placeholder}
      </span>
    );
  }

  return null;
}
